"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
} from "@/components/tool-kit/styles";
import CryptogramOutput from "@/components/tools/games-organizers/cryptogram-output";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildCryptogram,
  checkText,
  type GivenChoice,
  MAX_GIVEN,
  MAX_LETTERS,
  MIN_LETTERS,
  type TextProblem,
  type WordBreaks,
} from "@/lib/tool-kit/games-organizers/cryptogram";
import {
  findQuote,
  QUOTE_CATEGORIES,
  type QuoteCategory,
  quotesIn,
} from "@/lib/tool-kit/games-organizers/cryptogram-quotes";
import {
  DEFAULT_SEED,
  newSeed,
  parseSeed,
} from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const CRYPTOGRAM_GENERATOR_STRINGS = {
  sourceLabel: "Text to encipher",
  sources: { library: "Quotation library", custom: "My own text" },
  categoryLabel: "Collection",
  categories: {
    all: "All quotations",
    shakespeare: "Shakespeare",
    literature: "Literature and ideas",
    speeches: "Speeches and documents",
    proverbs: "Proverbs",
  },
  quoteLabel: "Quotation",
  randomQuote: "Random quotation",
  libraryNote:
    "Every quotation is public domain, by an author who died before 1955 or an anonymous proverb, and names the work or occasion it comes from.",
  customLabel: "Your text (letters A–Z; {min}–{max} letters)",
  customPlaceholder:
    "Type or paste a quotation, a vocabulary sentence or a class motto.",
  customAuthorLabel: "Author or source (optional)",
  customAuthorPlaceholder: "Anonymous",
  problems: {
    empty: "Type the text you want to encipher.",
    short: "Use at least {min} letters, or the puzzle gives away too much.",
    long: "Use at most {max} letters so the puzzle fits on a page.",
    foreign: "Letters outside A–Z cannot be enciphered here: {letters}",
  },
  givenLabel: "Given letters",
  givenOption: "one:{count} given letter|other:{count} given letters",
  givenChoiceLabel: "Which letters to give",
  givenChoices: { random: "Random ones", commonest: "The commonest" },
  breaksLabel: "Word breaks",
  breaks: {
    words: "Keep the words and punctuation",
    groups: "Five-letter groups, no punctuation (harder)",
  },
  showAuthor: "Print the author under the puzzle",
  showFrequency: "Print a table of cipher letter counts",
  nameLine: "Name and date line",
  newCipher: "New cipher",
  seedLabel: "Puzzle number",
  seedHint: "Type a number from 1 to 999999 to rebuild the same cipher.",
  seedInvalid: "Use a whole number from 1 to 999999.",
  fromSeed: "Make puzzle from this number",
  showKey: "Show answer key",
  hideKey: "Hide answer key",
  printPuzzle: "Print puzzle",
  printBoth: "Print puzzle and key",
  copyCipher: "Copy cipher text",
  copied: "Copied!",
  heading: "Your cryptogram",
  statsLine: "{letters} letters, {distinct} different",
  puzzleNumber: "Puzzle {seed}",
  sheetTitle: "Cryptogram",
  instructions:
    "Every letter of the quotation has been replaced by another letter, always the same one. Decode it by writing the real letters on the lines.",
  givenIntro: "Given letters: {pairs}",
  givenPair: "{cipher} = {plain}",
  byline: "— {author}",
  frequencyHeading: "Cipher letter counts",
  keyTitle: "Answer key",
  keyAuthor: "{author}, {source}",
  keyTableHeading: "Cipher alphabet",
  plainRow: "Plain",
  cipherRow: "Cipher",
  gridLabel: "Cryptogram: each cipher letter above a line for its plain letter",
  keyGridLabel: "Cryptogram with the answers written in",
  frequencyLabel: "How often each cipher letter occurs",
  name: "Name",
  date: "Date",
} as const;

type Strings = typeof CRYPTOGRAM_GENERATOR_STRINGS;
type Source = "library" | "custom";
type Category = QuoteCategory | "all";

const GIVEN_CHOICES = Array.from({ length: MAX_GIVEN + 1 }, (_, i) => i);
const FIRST_QUOTE = quotesIn("all")[0]?.id ?? "";
const PREVIEW_WORDS = 7;

function preview(text: string): string {
  const words = text.split(" ");
  return words.length > PREVIEW_WORDS
    ? `${words.slice(0, PREVIEW_WORDS).join(" ")}…`
    : text;
}

function problemText(
  strings: Strings,
  problem: TextProblem,
  letters: string[]
): string {
  return t(strings.problems[problem], {
    min: MIN_LETTERS,
    max: MAX_LETTERS,
    letters: letters.join(" "),
  });
}

export default function CryptogramGeneratorWidget() {
  const strings = useToolUi(CRYPTOGRAM_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [source, setSource] = useState<Source>("library");
  const [category, setCategory] = useState<Category>("all");
  const [quoteId, setQuoteId] = useState(FIRST_QUOTE);
  const [custom, setCustom] = useState("");
  const [customAuthor, setCustomAuthor] = useState("");
  const [given, setGiven] = useState(2);
  const [givenChoice, setGivenChoice] = useState<GivenChoice>("random");
  const [breaks, setBreaks] = useState<WordBreaks>("words");
  const [showAuthor, setShowAuthor] = useState(true);
  const [showFrequency, setShowFrequency] = useState(false);
  const [nameLine, setNameLine] = useState(true);
  const [seed, setSeed] = useState(DEFAULT_SEED);
  const [seedText, setSeedText] = useState(String(DEFAULT_SEED));
  const [seedError, setSeedError] = useState(false);

  const quote = findQuote(quoteId);
  const text = source === "library" ? (quote?.text ?? "") : custom;
  const author =
    source === "library" ? (quote?.author ?? "") : customAuthor.trim();
  const origin = source === "library" ? (quote?.source ?? "") : "";
  const check = checkText(text);
  const puzzle = useMemo(
    () => buildCryptogram({ text, seed, breaks, given, givenChoice }),
    [text, seed, breaks, given, givenChoice]
  );

  function choose(next: number) {
    setSeed(next);
    setSeedText(String(next));
    setSeedError(false);
  }

  function pickCategory(next: Category) {
    setCategory(next);
    if (!quotesIn(next).some((q) => q.id === quoteId)) {
      setQuoteId(quotesIn(next)[0]?.id ?? "");
    }
  }

  function randomQuote() {
    const pool = quotesIn(category);
    const buffer = new Uint32Array(1);
    globalThis.crypto.getRandomValues(buffer);
    setQuoteId(pool[(buffer[0] ?? 0) % pool.length]?.id ?? quoteId);
    choose(newSeed());
  }

  function fromSeed() {
    const parsed = parseSeed(seedText);
    if (parsed === null) {
      setSeedError(true);
      return;
    }
    choose(parsed);
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4 print:hidden">
        <PillGroup
          label={strings.sourceLabel}
          name="crypto-source"
          onChange={setSource}
          options={(["library", "custom"] as const).map((value) => ({
            value,
            label: strings.sources[value],
          }))}
          value={source}
        />
        {source === "library" ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                id="crypto-category"
                label={strings.categoryLabel}
                onChange={pickCategory}
                options={(["all", ...QUOTE_CATEGORIES] as const).map(
                  (value) => ({ value, label: strings.categories[value] })
                )}
                value={category}
              />
              <SelectField
                id="crypto-quote"
                label={strings.quoteLabel}
                onChange={setQuoteId}
                options={quotesIn(category).map((q) => ({
                  value: q.id,
                  label: `${q.author}: ${preview(q.text)}`,
                }))}
                value={quoteId}
              />
            </div>
            <button
              className={BUTTON_SECONDARY}
              onClick={randomQuote}
              type="button"
            >
              {strings.randomQuote}
            </button>
            <p className={HINT}>{strings.libraryNote}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <TextAreaField
              id="crypto-custom"
              label={t(strings.customLabel, {
                min: MIN_LETTERS,
                max: MAX_LETTERS,
              })}
              onChange={setCustom}
              placeholder={strings.customPlaceholder}
              prose
              value={custom}
            />
            <TextField
              id="crypto-custom-author"
              label={strings.customAuthorLabel}
              onChange={setCustomAuthor}
              placeholder={strings.customAuthorPlaceholder}
              value={customAuthor}
            />
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="crypto-given"
            label={strings.givenLabel}
            onChange={(value) => setGiven(Number(value))}
            options={GIVEN_CHOICES.map((n) => ({
              value: String(n),
              label: tPlural(strings.givenOption, locale, n, { count: fmt(n) }),
            }))}
            value={String(given)}
          />
          {given > 0 ? (
            <PillGroup
              label={strings.givenChoiceLabel}
              name="crypto-given-choice"
              onChange={setGivenChoice}
              options={(["random", "commonest"] as const).map((value) => ({
                value,
                label: strings.givenChoices[value],
              }))}
              value={givenChoice}
            />
          ) : null}
        </div>
        <PillGroup
          label={strings.breaksLabel}
          name="crypto-breaks"
          onChange={setBreaks}
          options={(["words", "groups"] as const).map((value) => ({
            value,
            label: strings.breaks[value],
          }))}
          value={breaks}
        />
        <fieldset className="space-y-2">
          <Checkbox
            checked={showAuthor}
            label={strings.showAuthor}
            onChange={setShowAuthor}
          />
          <Checkbox
            checked={showFrequency}
            label={strings.showFrequency}
            onChange={setShowFrequency}
          />
          <Checkbox
            checked={nameLine}
            label={strings.nameLine}
            onChange={setNameLine}
          />
        </fieldset>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            onClick={() => choose(newSeed())}
            type="button"
          >
            {strings.newCipher}
          </button>
        </div>
        <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto]">
          <TextField
            hint={strings.seedHint}
            id="crypto-seed"
            label={strings.seedLabel}
            mono
            numeric
            onChange={setSeedText}
            value={seedText}
          />
          <button className={BUTTON_SECONDARY} onClick={fromSeed} type="button">
            {strings.fromSeed}
          </button>
        </div>
        {seedError ? <ErrorNotice>{strings.seedInvalid}</ErrorNotice> : null}
      </div>

      {check.problem && !(source === "library") ? (
        <div aria-live="polite" className="print:hidden">
          <Notice>{problemText(strings, check.problem, check.foreign)}</Notice>
        </div>
      ) : null}

      {puzzle ? (
        <CryptogramOutput
          author={author}
          nameLine={nameLine}
          origin={origin}
          puzzle={puzzle}
          seed={seed}
          showAuthor={showAuthor}
          showFrequency={showFrequency}
          strings={strings}
        />
      ) : null}
    </div>
  );
}
